import {uiMsg} from '../utils/Languages';
import {observable, computed, autorun, map} from 'mobx';
import * as LoadLevel from './LoadLevel';
import RC from '../utils/ReactiveClock';

// For now this is just a blind copy of the rows from the database.
function createJobFromDBRow(row) {  
  var j = new Job();
  j.id_machine=row.id_machine;
  j.machine_name=row.machine_name;
  j.start_time = row.start_time==null ? null : new Date(row.start_time);
  j.end_time = row.end_time==null ? null : new Date(row.end_time);
  j.status = row.status;
  j.id_location = row.id_location;
  j.location_name = row.location_name;
  j.volume_m3 = row.volume_m3;
  if(!j.id_location) {
    j.location_name=row.job_location || row.location;
    j.volume_m3 = row.job_volume_m3;
  }
  j.user_id = row.user_id;
  j.user_name = row.user_name;
  j.fluid = row.fluid;
  j.fluid_id = row.fluid_id;

  j.seconds_remain = row.remaining_seconds;
  j.seconds_remain_set_at = row.remaining_seconds_update_ts==null ? null : new Date(row.remaining_seconds_update_ts);
  
  // This is the information we need to show a job in a list.
  // If we want to get DETAIL level, we also need to load the measurements
  // for the job.
  j.load_level=LoadLevel.LIST;
  j.building = row.building;

  j.job_type_id = row.id_job_type;
  j.job_type_name = row.job_type_name;

  if(row.phases) {
    j.phases = map();
    row.phases.forEach( phase => {
      j.phases.set(phase.name, new Date(phase.start_time));
    })
  }
  return j;
}

class Job {
  @observable id_machine;
  @observable machine_name;
  @observable machine_sensors_map = map();
  @observable start_time = null;
  @observable end_time = null;
  // If not null, and the job is running, than this number should indicate
  // how much longer the job will be running for.
  @observable seconds_remain = null;
  // The timestamp that the seconds_remain was set:
  @observable seconds_remain_set_at = null;

  @observable status = null;
  @observable id_location = null;
  // Location should be set by a valid locationRef, which will point to a
  // a valid location defined for the id_customer
  // For a period it will be possible to have freeform data in these two fields:
  @observable location_name = null;
  @observable volume_m3 = null;

  @observable job_type_id = null;
  @observable job_type_name = null;

  @observable user_id = null;
  @observable user_name = null;
  @observable fluid = null;
  @observable fluid_id = null;

  // If we load the full data-set for a job :
  @observable events = [];
  @observable alarms = [];
  // Measurements are stored as MEASURENAME => sensorindex => ARRAY of (Time, Value) tuples
  @observable measurements = map();
  // Timestamp of when last reading happened (based on newest returned from DB)
  // This can be used to verify that the machine hasn't gone offline.
  @observable last_measurement_time = null;
  // If the latests data update indicates that the machine has gone offline..
  @observable offline = false;
  // Phase info is stored as a PhaseName => start_time map of active phases.
  @observable phases = map();
  @observable complete_phaselist = []; // Detailed list of job phases from the DB
  // These are status fields that represent the status of loading data
  // the server for this machine
  @observable load_level;
  @observable loading;


  // If we fetch a job record and want to update an existing object
  updateFrom(otherJob) {
    // Only update fields that could/should change:
    this.end_time = otherJob.end_time ;
    this.status = otherJob.status;
    this.user_id = otherJob.user_id;
    this.user_name = otherJob.user_name;
    this.id_location = otherJob.id_location;
    this.location_name = otherJob.location_name;
    this.load_level = otherJob.load_level;
    this.machine_name = otherJob.machine_name;
    this.volume_m3 = otherJob.volume_m3;
    this.job_type_id = otherJob.job_type_id;
    this.job_type_name = otherJob.job_type_name;
    if(otherJob.phases) {
      this.phases = otherJob.phases;
    }
    // Other data is kept.
  }


  @computed get statusMsg() {
    // Picking the right status message for the job.
    if(this.running) return uiMsg('JB_JOB_IS_RUNNING',"Job is running");
    if(this.status=="SUCCESS" || this.status=="COMPLETED") {
      // If there was no alarms, then everything is perfect:
      if(!this.alarms || this.alarms.length==0) {
        return uiMsg('JB_SUCCESSFUL_DISINFECT_COMPLETED',
          "Successful disinfection completed");
      }
      // If the job completed successfully, but there are alarms, then
      // these were warnings :
      else {
        return uiMsg('JB_DISINFECTION_COMPLETE_WITH_WARNINGS',
          "Disinfection completed with warning(s)");
      }
    }
    else {
      return uiMsg('JB_DISINFECTION_DID_NOT_COMPLETE_SUCCESFULLY',
        "Disinfection did not complete successfully");
    }
  }


  @computed get remain_string() {
    if(!this.seconds_remain) return uiMsg('JB_TIME_REMAIN_UNKNOWN',"Time remaining unknown ");
    let seconds_since_update = (RC.reactiveTime - this.seconds_remain_set_at)/1000;
    let seconds = (this.seconds_remain-seconds_since_update);
    var h = Math.floor(seconds / 3600);
    var m = Math.floor(seconds % 3600 / 60);
    
    if(h<1.0 && m>=1.0) return uiMsg('JOB_MINUTES_REMAIN',"X minutes", m.toFixed(0));
    if(m<1.0) return uiMsg('JB_LESS_THAN_A_MINUTE',"less than 1 minute")
    return uiMsg('JOB_HOURS_MINUTES_REMAIN',"Y h X min", h.toFixed(0), m.toFixed(0));
  }

  @computed get minutes() {
    var start = +this.start_time;
    var end = +this.end_time;
    if(!this.end_time) {
      end = +(new Date());
    }
    return (end - start)/60/1000;
  }

  // Get the average measurement value for a sensor :
  avg_sensor(sensorIndex, sensor) {
    if(!this.measurements.has(sensorIndex) || !this.measurements.get(sensorIndex).has(sensor)) return null;
    var value = 0.0;
    var measures = this.measurements.get(sensorIndex).get(sensor);
    // Each measurement is a [Date, Value] array :
    measures.forEach( v => value = value + v[1]);
    return (value/measures.length).toFixed(1);
  }

  @computed get h2o2_realtime_avg_value() {

    let value = null;
    if(this.measurements){
      value = 0.0;
        this.measurements.forEach(function(sensor){
          if(sensor.has('H2O2')){
              let measures = sensor.get('H2O2');
              // Each measurement is a [Date, Value] array :
              measures.forEach( v => value = value + v[1]);
              value = (value/measures.length).toFixed(1);
          }
        });
    }

    return value;

  }

  @computed get avg_h202_for_all_sensors() {
    let result = [];
    this.machine_sensors_map.forEach(sensor => {
      result.push({
        index: sensor.sensor_index,
        name: sensor.name,
        avgValue: this.avg_sensor(sensor.sensor_index, "H2O2")
      })
    });
    return result;
  }

  /**
  * Computed properties and utility functions
  */

  addMeasurement(evName, evTime, evValue, evSensorIndex) {
    if(!this.measurements.has(evSensorIndex)){
      this.measurements.set(evSensorIndex,map());
    }
    if(!this.measurements.get(evSensorIndex).has(evName)) {
      this.measurements.get(evSensorIndex).set(evName,[]);
    }
    if(!this.measurements.get(evSensorIndex).get(evName).some(record =>
      record[0].getTime()==evTime.getTime()
    )) {
      this.measurements.get(evSensorIndex).get(evName).push([evTime,evValue]);
    }
  }

  // Returns true of this is job is completed, but less than 60 minutes ago
  @computed get recentlyActive() {
    if(this.end_time == null) return false;
    let minutes = (RC.reactiveTime - this.end_time.getTime())/(1000*60);
    return minutes< (process.env.NODE_ENV=='development' ? 3 : 60)
  }

  // Returns true if there are reasons to believe that this job has not been
  // ended as it should.  It should be removed at next machine-restart, but
  // meanwhile the UI should hide such jobs from home page as they are noisy.
  @computed get timedOut() {
    if(this.status=="SUCCESS") return false;
    // If the job has an end time, then is has been terminated
    if(this.end_time != null) return false;
    // If it hasn't started, it hasn't timed out (yet)
    if(this.start_time == null) return false;
    let minutes = (RC.reactiveTime - this.start_time.getTime())/(1000*60);
    // If this job was started more than 8 hours ago :
    let minutesBeforeExpiry = 60*8;
    return minutes > minutesBeforeExpiry
  }

  @computed get minutesSinceEnd() {
    if(this.end_time == null) return null;
    let minutes = (RC.reactiveTime - this.end_time.getTime())/(1000*60);
    return minutes;
  }

  @computed get lastMeasureReadTime() {
    // TODO we just check for the first sensorIndex and takes for granted that this is equal to all the sensors. Is this OK?
    if(!this.measurements.has(1)) return null;
    return Math.max.apply(null,this.measurements.get(1).keys().map( sensor =>
      this.getLastMeasureTimeFromSensor(1, sensor)
    ));
  }

  getLastMeasureTimeFromSensor(sensorIndex, sensor) {
    if(!this.measurements.has(sensorIndex) || !this.measurements.get(sensorIndex).has(sensor)) return null;
    var measures = this.measurements.get(sensorIndex).get(sensor);
    // Each measure is a [date, value] array:
    return measures[measures.length-1][0]
  }

  getLastMeasureFromSensor(sensorIndex, sensor) {
    if(!this.measurements.has(sensorIndex) || !this.measurements.get(sensorIndex).has(sensor)) return null;
    var measures = this.measurements.get(sensorIndex).get(sensor);
    // Each measure is a [date, value] array:
    return measures[measures.length-1][1]
  }

  @computed get last_h2o2_for_all_sensors() {
    let result = [];

    this.machine_sensors_map.forEach(sensor => {
      result.push({
        index: sensor.sensor_index,
        name: sensor.name,
        value: this.getLastMeasureFromSensor(sensor.sensor_index, "H2O2")
      })
    });

    return result;
  }



  @computed get h2o2_level() {

      var lastMeasure = null;

      if(this.measurements){
          lastMeasure = 0.0;
          this.measurements.forEach(function(sensor){
              if(sensor.has('H2O2')){
                  let measures = sensor.get('H2O2');
                  if(measures.length > 0){
                      let lastExt = measures[measures.length-1];
                      if(lastExt[1] > lastMeasure) {
                          lastMeasure = lastExt[1];
                      }
                  }
              }
          });
      }

      return lastMeasure.toFixed(1);

  }

  // This is not a computed property, as it relies on the Timestamp of the
  // check.  It is run once after each update of the
  // last_measurement_time
  checkIfOffline() {
    // If we have not received measurements in 5 mintes.
    var FiveMinutesAgo = new Date(new Date()-1000*60*5);
    // console.log("FiveMinutesAgo=",FiveMinutesAgo);
    // console.log("last_measurement_time=",this.last_measurement_time);
    if(this.last_measurement_time<FiveMinutesAgo) {
      console.log("Flagging job offline");
      this.offline=true;
    }
  }

  // Phase utility functions:
  startPhase(name, ts) {
    console.log("Starting phase");
    this.phases.set(name,ts);
  }
  endPhase(name, ts) {
    if(this.phases.has(name)) {
      this.phases.delete(name);
    }
  }
  // Computed properties
  @computed get activePhases() {
    console.log("Computing activePhases");
    var phaseList = [];
    this.phases.keys().forEach( (phaseName) => {
      // Add timestamp and phasename tuple to phaseList;
      phaseList.push([this.phases.get(phaseName),phaseName]);
    });
    // Sort the phases by start time :
    phaseList.sort();
    // Return named phases in the order they were started :
    return phaseList.map( (tuple) => {return tuple[1]});
  }

  @computed get running() {
    if(this.status=="SUCCESS") return false;
    if(this.timedOut) return false;
    if (this.start_time!=null &&
        this.end_time==null) return true;
    return false;
  }

  @computed get end_time_str() {
    if(!this.end_time) {
      return "";
    }
    return this.end_time.toLocaleString();
  }
  @computed get start_time_str() {
    return this.start_time.toLocaleString();
  }
  @computed get key() {
    if(!this.start_time) {
      console.error("this.start_time in job undefined or null");
    }
    return this.id_machine+this.start_time.getTime();
  }
  @computed get debugLine() {
    return this.id_machine+this.start_time+this.status;
  }

  // We just copy the measurement arrays over to the reactive map:
  importMeasurements(newmeasurements) {

      let measurementsCopy = Object.assign({}, newmeasurements);

      for (const sensorIndex of Object.keys(measurementsCopy)) {
          let sensor = measurementsCopy[sensorIndex];
          let sensorMap = map();
          for (const key of Object.keys(sensor)) {
              let measurements = sensor[key];
              for (let measure of measurements) {
                  measure[0] = new Date(measure[0]);
              }
              sensorMap.set(key, sensor[key])
          }

          this.measurements.set(sensorIndex, sensorMap);
      }
  }

  // Import sensors and create map with sensor_index as key for faster
  importSensors(sensors) {
    this.machine_sensors_map = map();
    for (const sensor of sensors) {
      this.machine_sensors_map.set(sensor.sensor_index, sensor)
    }
  }

  importAlarms(newalarms) {
    newalarms.forEach( a => a.ts = new Date(a.ts));
    this.alarms = newalarms;
  }

  importPhases(newphases) {
    console.log("Importing phases from ",newphases);
    newphases.forEach( p => {
      if(p.start_time) p.start_time = new Date(p.start_time);
      if(p.end_time) p.end_time = new Date(p.end_time);
    });
    this.complete_phaselist = newphases;
  }

}

// Make utility method available from Class (function)
Job.createFromDBRow=createJobFromDBRow;

export default Job;



/** WEBPACK FOOTER **
 ** ./src/DataModel/Job.js
 **/